/**
 * Helpers for the GEN-2608 "phenotypes may be stale" banner shown in the
 * edit-order flow.
 *
 * `OrderInProgress` already maintains a serialized baseline of the clinical
 * summary at the moment of the last successful send (`baselineClinicalRef`).
 * The serialized form is JSON-encoded by `serializeClinicalBaseline`
 * (clinical note plus differential diagnosis for dirty detection).
 *
 * The stale-HPO banner only cares about the **clinical note**: phenotype
 * extraction ignores differential diagnosis, so the banner compares the
 * baseline note to the current note — not the full serialized clinical slice.
 *
 * This module decodes the serialized baseline back into the stored clinical
 * note string. Kept as a small pure helper so the parsing path can be
 * unit-tested without spinning up the full OrderInProgress page.
 */

interface ClinicalBaselineShape {
  clinicalNote?: string
}

export function parseClinicalBaselineSummary(serialized: string | null | undefined): string {
  if (!serialized) return ''
  try {
    const parsed = JSON.parse(serialized) as ClinicalBaselineShape
    if (parsed && typeof parsed === 'object' && typeof parsed.clinicalNote === 'string') {
      return parsed.clinicalNote
    }
    return ''
  } catch {
    return ''
  }
}

/**
 * True when there is a non-empty last-sent clinical **note** in the baseline
 * and the user's current note differs. Differential-diagnosis edits alone do
 * not surface the banner (phenotype extraction does not use that field).
 */
export function shouldShowStaleHpoBanner(previousSentClinicalNote: string, currentClinicalNote: string): boolean {
  const previous = previousSentClinicalNote.trim()
  if (previous.length === 0) {
    return false
  }
  return currentClinicalNote.trim() !== previous
}
